/**
 * Protected route wrapper.
 *
 * Normal flow: requires an authenticated AFI session (from CRM login).
 *
 * Embed flow (iframe): if there is no session but the URL carries a VDL user
 * token (`?token=`), exchange it for a short-lived AFI session via
 * POST /api/embed/exchange, keep it in the auth store, and strip the token
 * from the URL. This lets the existing Swiper URL work inside an <iframe>
 * on a trusted external page without an AFI login screen.
 */

import { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import { api } from '@/services/api'

type ExchangeStatus = 'idle' | 'exchanging' | 'failed'

/** Remove the VDL token from both the query string and the URL #fragment. */
function stripEmbedTokenFromUrl() {
  const url = new URL(window.location.href)
  url.searchParams.delete('token')
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''))
  hashParams.delete('token')
  const nextHash = hashParams.toString()
  url.hash = nextHash ? `#${nextHash}` : ''
  window.history.replaceState({}, '', url.toString())
}

function ProtectedRoute() {
  const { isAuthenticated, setAuth } = useAuthStore()

  // * VDL token supplied by the embedding page (read once on mount).
  // * Prefer the URL #fragment (never sent to the server → not in nginx logs / Referer);
  // * fall back to ?token= query for simple static-iframe embedding.
  const [embedToken] = useState<string | null>(() => {
    const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')
    const fromQuery = new URLSearchParams(window.location.search).get('token')
    return fromHash || fromQuery
  })
  const [status, setStatus] = useState<ExchangeStatus>('idle')

  useEffect(() => {
    if (isAuthenticated || !embedToken) return

    let cancelled = false
    setStatus('exchanging')

    ;(async () => {
      try {
        // * crm_id lives in the path: /projects/:crmId/sources/swipe
        const match = window.location.pathname.match(/\/projects\/(\d+)/)
        const crmId = match ? Number(match[1]) : undefined

        const data = await api.embedExchange(embedToken, crmId)
        if (cancelled) return

        // * Persist via authStore (localStorage, partitioned per top-level origin in a
        // * cross-domain iframe). No refresh token is issued for embed sessions.
        setAuth(data.access_token, '', data.operator)

        // * Remove the VDL token from the URL (nginx logs / history / referrer)
        stripEmbedTokenFromUrl()

        setStatus('idle')
      } catch {
        // * Strip the rejected token from the URL too before falling back to login
        stripEmbedTokenFromUrl()
        if (!cancelled) setStatus('failed')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [embedToken, isAuthenticated, setAuth])

  if (isAuthenticated) {
    return <Outlet />
  }

  // * Embed exchange in progress — show a spinner instead of the login redirect
  if (embedToken && status !== 'failed') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spin size="large" tip="Авторизация..." />
      </div>
    )
  }

  return <Navigate to="/login" replace />
}

export default ProtectedRoute
